import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Play, Target, Brain, ArrowRight, Star, AlertTriangle,
  Calculator, FileText, ChevronDown, ChevronUp,
  PenTool, Layers, RotateCcw, TrendingUp, Award, Youtube
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'

// ────────────────────────────────────────────────────────────────────────────
// 문제 데이터 타입
// ────────────────────────────────────────────────────────────────────────────
interface Step {
  label: string
  content: string
}

interface Problem {
  id: string
  level: 'basic' | 'standard' | 'advanced' | 'final'
  number: number
  tag: string
  question: string
  choices?: string[]          // 객관식이면 보기 배열
  answer: string              // 객관식이면 "①" 형태, 주관식이면 값
  solutionSteps: Step[]
  keyFormula?: string
  tip?: string
  concept: string             // 연결 개념
}

// ────────────────────────────────────────────────────────────────────────────
// 문제 세트 (EBS 스타일 — 기본 → 실력 → 심화 → 종합)
// ────────────────────────────────────────────────────────────────────────────
const PROBLEMS: Problem[] = [
  // ── 기본 문제 (BASIC) ────────────────────────────────────────────────────
  {
    id: 'P01', level: 'basic', number: 1,
    tag: '변수·기울기', concept: '기울기',
    question: 'y = 3x − 5 에서 기울기와 y절편을 각각 구하시오.',
    answer: '기울기: 3,  y절편: −5',
    solutionSteps: [
      { label: '① 표준형 확인', content: 'y = ax + b 형태에서 a = 기울기, b = y절편' },
      { label: '② 계수 읽기', content: 'y = 3x + (−5) 이므로 a = 3, b = −5' },
      { label: '③ 결론', content: '기울기 = 3 (x가 1 증가하면 y가 3 증가)\ny절편 = −5 (그래프가 y축의 (0, −5)를 지남)' },
    ],
    keyFormula: 'y = ax + b → 기울기: a, y절편: b',
    tip: 'ax 앞의 계수가 기울기! b가 y절편!',
  },
  {
    id: 'P02', level: 'basic', number: 2,
    tag: '순서쌍·좌표', concept: '좌표평면',
    question: '점 A(−3, 4)와 점 B(2, −1)은 각각 몇 사분면에 있는가?',
    answer: 'A: 제2사분면,  B: 제4사분면',
    solutionSteps: [
      { label: '① 사분면 규칙', content: '1사분면(+,+) / 2사분면(−,+) / 3사분면(−,−) / 4사분면(+,−)' },
      { label: '② A(−3, 4) 판단', content: 'x = −3 < 0,  y = 4 > 0  →  제2사분면' },
      { label: '③ B(2, −1) 판단', content: 'x = 2 > 0,  y = −1 < 0  →  제4사분면' },
    ],
    keyFormula: '(−, +) → 2사분면  /  (+, −) → 4사분면',
    tip: 'x 부호 먼저, y 부호 나중에 확인!',
  },
  {
    id: 'P03', level: 'basic', number: 3,
    tag: '함수식 세우기', concept: '일차함수 식',
    question: '기울기가 2이고, 점 (1, 5)를 지나는 일차함수의 식을 구하시오.',
    answer: 'y = 2x + 3',
    solutionSteps: [
      { label: '① 형태 설정', content: '기울기 2 → y = 2x + b 로 놓는다' },
      { label: '② b 결정', content: '점 (1, 5) 대입: 5 = 2×1 + b\n5 = 2 + b → b = 3' },
      { label: '③ 함수식 완성', content: 'y = 2x + 3' },
      { label: '④ 검증', content: 'x=1 대입: y = 2(1)+3 = 5 ✓' },
    ],
    keyFormula: '점 (x₀, y₀)과 기울기 a → y₀ = ax₀ + b → b 계산',
    tip: '알려진 점의 좌표를 y = ax + b에 대입하여 b를 구하면 됩니다!',
  },
  {
    id: 'P04', level: 'basic', number: 4,
    tag: 'x절편·y절편', concept: 'x절편·y절편',
    question: 'y = −2x + 6 의 x절편과 y절편을 구하시오.',
    choices: [
      '① x절편: 3,  y절편: 6',
      '② x절편: 6,  y절편: 3',
      '③ x절편: −3,  y절편: 6',
      '④ x절편: 3,  y절편: −6',
    ],
    answer: '①',
    solutionSteps: [
      { label: '① y절편 구하기 (x=0 대입)', content: 'y = −2(0) + 6 = 6\n→ y절편 = 6,  좌표 (0, 6)' },
      { label: '② x절편 구하기 (y=0 대입)', content: '0 = −2x + 6\n2x = 6 → x = 3\n→ x절편 = 3,  좌표 (3, 0)' },
      { label: '③ 정답 확인', content: 'x절편 = 3,  y절편 = 6  →  ①번' },
    ],
    keyFormula: 'y절편: x=0 대입 / x절편: y=0 대입',
    tip: '절편은 축과 만나는 점의 좌표! x절편은 x축, y절편은 y축과의 교점',
  },

  // ── 실력 문제 (STANDARD) ─────────────────────────────────────────────────
  {
    id: 'P05', level: 'standard', number: 5,
    tag: '두 점으로 식 구하기', concept: '일차함수 식',
    question: '두 점 (−1, 7)과 (3, −1)을 지나는 일차함수의 식을 구하시오.',
    answer: 'y = −2x + 5',
    solutionSteps: [
      { label: '① 기울기 계산', content: 'a = (y₂ − y₁) / (x₂ − x₁)\n= (−1 − 7) / (3 − (−1))\n= −8 / 4 = −2' },
      { label: '② y절편 계산', content: 'y = −2x + b 에 점 (−1, 7) 대입\n7 = −2×(−1) + b\n7 = 2 + b → b = 5' },
      { label: '③ 함수식 완성', content: 'y = −2x + 5' },
      { label: '④ 검증', content: '(3, −1) 대입: y = −2(3)+5 = −6+5 = −1 ✓' },
    ],
    keyFormula: 'a = (y₂−y₁)/(x₂−x₁),  이후 한 점 대입하여 b 계산',
    tip: '두 점 중 어느 점을 대입해도 같은 b가 나옵니다. 계산이 편한 점을 고르세요!',
  },
  {
    id: 'P06', level: 'standard', number: 6,
    tag: '그래프 해석', concept: '그래프 읽기',
    question: '아래 그래프가 두 점 (0, 3)과 (4, −1)을 지날 때, 이 직선의 식과 x절편을 구하시오.',
    answer: 'y = −x + 3,  x절편 = 3',
    solutionSteps: [
      { label: '① y절편 읽기', content: '(0, 3)을 지나므로 y절편 b = 3' },
      { label: '② 기울기 계산', content: 'a = (−1 − 3)/(4 − 0) = −4/4 = −1' },
      { label: '③ 함수식 완성', content: 'y = −x + 3' },
      { label: '④ x절편 구하기', content: 'y=0 대입: 0 = −x + 3 → x = 3\nx절편 = 3' },
    ],
    keyFormula: '그래프 → 식: y절편 읽기 → 기울기 계산 → 식 완성',
    tip: 'y축과 만나는 점이 y절편입니다. (0, b) 형태로 주어지면 즉시 b를 알 수 있어요!',
  },
  {
    id: 'P07', level: 'standard', number: 7,
    tag: '실생활 문장제', concept: '함수 모델링',
    question: '달리기를 시작한 지 x분 후에 소비한 칼로리가 y kcal이다.\n시작 3분 후에 48 kcal, 8분 후에 128 kcal를 소비했을 때,\n① 일차함수의 식을 구하고  ② 200 kcal를 소비하는 데 걸리는 시간을 구하시오.',
    answer: '① y = 16x  ② 12.5분',
    solutionSteps: [
      { label: '① 기울기 계산 (변화율)', content: 'a = (128 − 48)/(8 − 3) = 80/5 = 16 (kcal/분)' },
      { label: '② y절편 계산', content: 'y = 16x + b 에 (3, 48) 대입:\n48 = 16×3 + b = 48 + b → b = 0' },
      { label: '③ 함수식 완성', content: 'y = 16x  (정비례! 원점을 지남)' },
      { label: '④ 200 kcal 소비 시간', content: '200 = 16x → x = 200/16 = 12.5분' },
    ],
    keyFormula: '변화율 = Δy/Δx = 칼로리 변화량/시간 변화량',
    tip: 'y절편이 0이면 정비례 함수(원점 통과)! 실생활에서 "처음부터 소비"라는 의미입니다.',
  },
  {
    id: 'P08', level: 'standard', number: 8,
    tag: '기울기 비교', concept: '기울기·방향성',
    question: '다음 중 기울기가 가장 가파른(급격히 변하는) 함수는?\n① y = 2x + 1\n② y = −4x + 3\n③ y = 0.5x − 2\n④ y = 3x + 7',
    choices: [
      '① y = 2x + 1',
      '② y = −4x + 3',
      '③ y = 0.5x − 2',
      '④ y = 3x + 7',
    ],
    answer: '②',
    solutionSteps: [
      { label: '① 기울기 절댓값 비교', content: '|2| = 2\n|−4| = 4  ← 가장 큼!\n|0.5| = 0.5\n|3| = 3' },
      { label: '② 결론', content: '기울기의 절댓값이 클수록 더 가파름\n|−4| = 4 가 가장 크므로 ② y = −4x + 3 이 가장 가파름' },
    ],
    keyFormula: '기울기의 가파름 = |a| 의 크기 (절댓값 비교)',
    tip: '양수/음수 관계없이 절댓값이 클수록 더 가파른 직선입니다!',
  },

  // ── 심화 문제 (ADVANCED) ─────────────────────────────────────────────────
  {
    id: 'P09', level: 'advanced', number: 9,
    tag: '두 함수 교점', concept: '연립방정식 활용',
    question: '두 일차함수 y = 2x + 1 과 y = −x + 7 의 그래프의 교점의 좌표를 구하시오.',
    answer: '(2, 5)',
    solutionSteps: [
      { label: '① 연립방정식 세우기', content: '두 함수가 같을 때 교점\n2x + 1 = −x + 7' },
      { label: '② x 값 구하기', content: '2x + x = 7 − 1\n3x = 6 → x = 2' },
      { label: '③ y 값 구하기', content: 'x=2 를 첫 번째 식에 대입:\ny = 2(2) + 1 = 5' },
      { label: '④ 검증', content: '두 번째 식: y = −2 + 7 = 5 ✓\n교점 = (2, 5)' },
    ],
    keyFormula: '교점: 두 함수를 연립하여 x 구하기',
    tip: '두 함수를 같다고 놓고 방정식을 풀면 교점의 x좌표가 나옵니다!',
  },
  {
    id: 'P10', level: 'advanced', number: 10,
    tag: '평행 조건', concept: '평행·기울기',
    question: 'y = 3x + k 가 직선 y = 3x − 2 와 평행하고, 점 (1, 7)을 지날 때 k의 값을 구하시오.',
    answer: 'k = 4',
    solutionSteps: [
      { label: '① 평행 조건 확인', content: '평행한 두 직선은 기울기가 같고 y절편이 다름\n이미 기울기가 둘 다 3으로 같음 → 평행 조건 성립' },
      { label: '② 점 (1, 7) 대입', content: 'y = 3x + k 에 x=1, y=7 대입:\n7 = 3(1) + k = 3 + k → k = 4' },
      { label: '③ 검증', content: 'y = 3x + 4 와 y = 3x − 2:\n기울기 같음(3), y절편 다름(4 ≠ −2) → 평행 확인 ✓' },
    ],
    keyFormula: '평행 조건: 기울기 같음(a₁ = a₂), y절편 다름(b₁ ≠ b₂)',
    tip: '평행이면 기울기가 같아야 합니다. k는 알려진 점을 대입하여 결정합니다!',
  },
  {
    id: 'P11', level: 'advanced', number: 11,
    tag: '실생활 종합', concept: '함수 모델링',
    question: '어느 양초는 처음 길이가 20 cm이고, 1분마다 0.4 cm씩 짧아진다.\n① 양초의 길이 y(cm)를 x(분)의 함수로 나타내시오.\n② 양초의 길이가 12 cm가 되는 시간을 구하시오.\n③ 양초가 완전히 타는 시간을 구하시오.',
    answer: '① y = −0.4x + 20\n② 20분 후\n③ 50분 후',
    solutionSteps: [
      { label: '① 변수 설정', content: 'x: 시간(분), y: 양초 길이(cm)' },
      { label: '② 함수식 세우기', content: '기울기: −0.4(1분마다 0.4씩 감소)\ny절편: 20(처음 길이)\n→ y = −0.4x + 20' },
      { label: '③ 12 cm가 되는 시간', content: '12 = −0.4x + 20\n0.4x = 8 → x = 20분' },
      { label: '④ 완전히 타는 시간', content: 'y = 0 대입:\n0 = −0.4x + 20\n0.4x = 20 → x = 50분' },
    ],
    keyFormula: '"감소" → 음수 기울기 / "처음값" → y절편',
    tip: '문장제 키워드: "씩 줄어든다" = 음수 기울기, "처음에" = y절편!',
  },
  {
    id: 'P12', level: 'advanced', number: 12,
    tag: '그래프와 넓이', concept: '넓이 계산 응용',
    question: 'y = 2x − 4 의 그래프와 x축, y축으로 둘러싸인 삼각형의 넓이를 구하시오.',
    answer: '4',
    solutionSteps: [
      { label: '① x절편 구하기', content: 'y=0: 0 = 2x−4 → x=2\n→ x절편 = 2, 교점 (2, 0)' },
      { label: '② y절편 구하기', content: 'x=0: y = 2(0)−4 = −4\n→ y절편 = −4, 교점 (0, −4)' },
      { label: '③ 삼각형 꼭짓점 파악', content: '꼭짓점 세 개:\n원점 O(0,0), (2,0), (0,−4)' },
      { label: '④ 넓이 계산', content: '밑변 = 2 (x축 위, 0~2 구간)\n높이 = 4 (y축 위, 0~−4 절댓값)\n넓이 = (1/2) × 2 × 4 = 4' },
    ],
    keyFormula: '삼각형 넓이 = (1/2) × 밑변 × 높이',
    tip: 'x절편과 y절편의 절댓값이 각각 밑변과 높이가 됩니다!',
  },

  // ── 종합 문제 (FINAL) ────────────────────────────────────────────────────
  {
    id: 'P13', level: 'final', number: 13,
    tag: '종합 추론', concept: '종합',
    question: '일차함수 y = ax + b 에 대해 다음 조건이 주어졌을 때, a + b 의 값을 구하시오.\n• 직선이 점 (2, 3)과 점 (5, 9)를 지난다.',
    answer: 'a + b = 2 + (−1) = 1',
    solutionSteps: [
      { label: '① 기울기 계산', content: 'a = (9−3)/(5−2) = 6/3 = 2' },
      { label: '② y절편 계산', content: 'y = 2x + b 에 (2, 3) 대입:\n3 = 2×2 + b = 4 + b → b = −1' },
      { label: '③ a + b 계산', content: 'a + b = 2 + (−1) = 1' },
      { label: '④ 함수식 확인', content: 'y = 2x − 1\n(5, 9) 검증: y = 2(5)−1 = 9 ✓' },
    ],
    keyFormula: '두 점 → 기울기 → y절편 → a+b',
    tip: '문제에서 a+b, a−b 등 식의 값을 구할 때는 a와 b를 각각 구한 후 계산합니다!',
  },
  {
    id: 'P14', level: 'final', number: 14,
    tag: '조건 추론', concept: '종합',
    question: '다음 조건을 모두 만족하는 일차함수의 식을 구하시오.\n① x의 값이 3만큼 증가하면 y의 값이 6만큼 감소한다.\n② 그래프가 점 (0, 4)를 지난다.',
    answer: 'y = −2x + 4',
    solutionSteps: [
      { label: '① 기울기 결정 (조건①)', content: 'Δy/Δx = −6/3 = −2\n→ 기울기 a = −2 (감소이므로 음수!)' },
      { label: '② y절편 결정 (조건②)', content: '(0, 4)를 지나므로 y절편 b = 4' },
      { label: '③ 함수식 완성', content: 'y = −2x + 4' },
    ],
    keyFormula: '"감소" → 음수 기울기  /  (0, b) 통과 → y절편 = b',
    tip: '"y의 값이 6만큼 감소" = Δy = −6 임을 기억하세요!',
  },
  {
    id: 'P15', level: 'final', number: 15,
    tag: '종합 서술형', concept: '종합',
    question: '[서술형] 일차함수 y = −3x + 9 에 대해 다음을 구하시오.\n① x절편과 y절편\n② 그래프가 지나는 사분면\n③ x = −1 일 때 y의 값',
    answer: '① x절편=3, y절편=9\n② 1·2·4사분면\n③ y=12',
    solutionSteps: [
      { label: '① x절편·y절편', content: 'y절편 (x=0): y = −3(0)+9 = 9  → 점 (0, 9)\nx절편 (y=0): 0 = −3x+9 → x = 3  → 점 (3, 0)' },
      { label: '② 사분면 분석', content: '기울기 −3 < 0  → 오른쪽으로 갈수록 감소\ny절편 9 > 0  → y축의 위쪽을 지남\nx절편 3 > 0\n\n따라서: 1사분면(+,+), 2사분면(−,+), 4사분면(+,−) 통과\n3사분면은 통과하지 않음!' },
      { label: '③ x=−1 대입', content: 'y = −3×(−1) + 9 = 3 + 9 = 12' },
    ],
    keyFormula: '사분면 판단: 기울기 부호 + y절편 부호로 결정',
    tip: '음수 기울기의 그래프는 3사분면을 통과하지 않습니다. 양수 기울기는 2사분면을 통과하지 않아요!',
  },
]

// ────────────────────────────────────────────────────────────────────────────
// 레벨 설정
// ────────────────────────────────────────────────────────────────────────────
const LEVEL_CONFIG = {
  basic:    { label: '기본', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', header: 'bg-emerald-500', light: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-500', icon: '🟢', desc: '개념 확인 문제' },
  standard: { label: '실력', color: 'bg-blue-100 text-blue-700 border-blue-200',         header: 'bg-blue-500',    light: 'bg-blue-50 border-blue-200',    badge: 'bg-blue-500',    icon: '🔵', desc: '개념 응용 문제' },
  advanced: { label: '심화', color: 'bg-orange-100 text-orange-700 border-orange-200',   header: 'bg-orange-500',  light: 'bg-orange-50 border-orange-200',  badge: 'bg-orange-500',  icon: '🟠', desc: '종합 사고 문제' },
  final:    { label: '종합', color: 'bg-red-100 text-red-700 border-red-200',            header: 'bg-red-500',     light: 'bg-red-50 border-red-200',      badge: 'bg-red-500',     icon: '🔴', desc: '서술형·추론 문제' },
}

// ────────────────────────────────────────────────────────────────────────────
// 단일 문제 카드 컴포넌트
// ────────────────────────────────────────────────────────────────────────────
function ProblemCard({ prob, isOpen, onToggle, onSolved }: {
  prob: Problem
  isOpen: boolean
  onToggle: () => void
  onSolved?: (problemId: string, opts: { hintOpened: boolean; solutionOpened: boolean }) => void
}) {
  const [showHint, setShowHint] = useState(false)
  const [showSolution, setShowSolution] = useState(false)
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null)
  const [answered, setAnswered] = useState(false)
  const [videoModal, setVideoModal] = useState(false)
  const lv = LEVEL_CONFIG[prob.level]
  const getApprovedByTag   = useVideoStore(s => s.getApprovedByTag)
  const getRecruitingByTag = useVideoStore(s => s.getRecruitingByTag)
  const approvedVideos     = getApprovedByTag(prob.concept)
  const recruitingVideos   = getRecruitingByTag(prob.concept)

  const handleChoice = (choice: string) => {
    if (answered) return
    setSelectedChoice(choice)
    setAnswered(true)
    // progressStore에 풀이 기록
    onSolved?.(prob.id, { hintOpened: showHint, solutionOpened: showSolution })
  }

  const handleShowSolution = () => {
    setShowSolution(true)
    // 주관식: 풀이 볼 때도 기록
    if (!prob.choices) {
      onSolved?.(prob.id, { hintOpened: showHint, solutionOpened: true })
    }
  }

  const isCorrect = selectedChoice !== null && selectedChoice.startsWith(prob.answer)

  return (
    <div className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
      isOpen ? lv.light : 'border-slate-200 bg-white'
    }`}>
      {/* 문제 헤더 */}
      <button
        onClick={onToggle}
        className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50/80 transition-colors"
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-black flex-shrink-0 ${lv.badge}`}>
          {prob.number}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${lv.color}`}>
              {lv.icon} {lv.label}
            </span>
            <span className="text-xs text-slate-400 font-mono">{prob.tag}</span>
          </div>
          <p className="text-sm font-semibold text-slate-800 leading-relaxed whitespace-pre-line line-clamp-2">
            {prob.question}
          </p>
        </div>
        <div className="flex-shrink-0 mt-1">
          {isOpen
            ? <ChevronUp size={18} className="text-slate-400" />
            : <ChevronDown size={18} className="text-slate-400" />
          }
        </div>
      </button>

      {/* 펼쳐진 내용 */}
      {isOpen && (
        <div className="border-t border-slate-200">
          {/* 문제 본문 */}
          <div className="p-4 bg-white">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <p className="text-sm font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                <FileText size={13} /> 문제
              </p>
              <p className="text-base text-slate-800 leading-relaxed whitespace-pre-line font-medium">
                {prob.question}
              </p>
            </div>

            {/* 객관식 보기 */}
            {prob.choices && (
              <div className="mt-3 space-y-2">
                {prob.choices.map((choice, i) => {
                  const choiceNum = `${['①','②','③','④'][i]}`
                  const isSelected = selectedChoice === choiceNum || (selectedChoice && selectedChoice.startsWith(choiceNum))
                  const isThisCorrect = prob.answer === choiceNum
                  return (
                    <button
                      key={i}
                      onClick={() => handleChoice(choiceNum)}
                      className={`w-full text-left px-4 py-2.5 rounded-xl border-2 text-sm transition-all ${
                        !answered
                          ? 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                          : isThisCorrect
                            ? 'border-emerald-400 bg-emerald-50 text-emerald-800'
                            : isSelected && !isThisCorrect
                              ? 'border-red-300 bg-red-50 text-red-700'
                              : 'border-slate-100 bg-slate-50 text-slate-400'
                      }`}
                    >
                      <span className="font-bold mr-2">{choiceNum}</span>
                      {choice.replace(/^[①②③④]\s*/, '')}
                      {answered && isThisCorrect && <span className="ml-2 text-emerald-600 font-bold">✓ 정답</span>}
                      {answered && isSelected && !isThisCorrect && <span className="ml-2 text-red-500 font-bold">✗</span>}
                    </button>
                  )
                })}
              </div>
            )}

            {/* 주관식 정답 박스 */}
            {!prob.choices && (
              <div className="mt-3 flex gap-2">
                {!showSolution && (
                  <button
                    onClick={handleShowSolution}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                  >
                    <CheckCircle size={12} /> 정답 확인
                  </button>
                )}
              </div>
            )}

            {/* 주관식 정답 표시 */}
            {!prob.choices && showSolution && (
              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-xs font-bold text-emerald-600 mb-1">✅ 정답</p>
                <p className="text-sm font-bold text-emerald-800 whitespace-pre-line">{prob.answer}</p>
              </div>
            )}
          </div>

          {/* 힌트 + 풀이 버튼 영역 */}
          <div className="px-4 pb-2 flex gap-2 flex-wrap">
            {prob.tip && (
              <button
                onClick={() => setShowHint(!showHint)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
              >
                <Lightbulb size={11} /> {showHint ? '힌트 숨기기' : '힌트 보기'}
              </button>
            )}
            <button
              onClick={() => setShowSolution(!showSolution)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
            >
              <PenTool size={11} /> {showSolution ? '풀이 숨기기' : '단계별 풀이 보기'}
            </button>
          </div>

          {/* 힌트 */}
          {showHint && prob.tip && (
            <div className="mx-4 mb-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs font-bold text-amber-600 mb-1 flex items-center gap-1">
                <Lightbulb size={11} /> 핵심 힌트
              </p>
              <p className="text-sm text-amber-800">{prob.tip}</p>
              {prob.keyFormula && (
                <div className="mt-2 bg-amber-100 rounded-lg px-3 py-1.5">
                  <p className="text-xs font-mono text-amber-700">📐 {prob.keyFormula}</p>
                </div>
              )}
            </div>
          )}

          {/* 단계별 풀이 */}
          {showSolution && (
            <div className="mx-4 mb-4">
              <div className={`rounded-xl border-2 ${lv.light} p-4`}>
                <p className="text-xs font-bold text-slate-600 mb-3 flex items-center gap-1.5">
                  <PenTool size={12} /> 단계별 풀이 과정
                </p>
                <div className="space-y-3">
                  {prob.solutionSteps.map((step, si) => (
                    <div key={si} className="flex gap-3">
                      <div className={`w-6 h-6 rounded-full ${lv.badge} text-white text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        {si + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-bold text-slate-700 mb-0.5">{step.label}</p>
                        <div className="bg-white rounded-lg px-3 py-2 border border-slate-100">
                          <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{step.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 정답 표시 */}
                <div className="mt-4 bg-white rounded-xl p-3 border-2 border-emerald-300">
                  <p className="text-xs font-bold text-emerald-600 mb-1">✅ 최종 정답</p>
                  <p className="text-sm font-black text-emerald-800 whitespace-pre-line">{prob.answer}</p>
                </div>

                {/* 연결 개념 + 영상 복습 버튼 */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-400">연결 개념:</span>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">{prob.concept}</span>
                  {/* ① approved → 빨간, ② recruiting → 보라, ③ 없음 → 회색 */}
                  {approvedVideos.length > 0 ? (
                    <button
                      onClick={() => setVideoModal(true)}
                      className="ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-bold transition-all shadow-sm bg-gradient-to-r from-red-500 to-rose-500 text-white hover:opacity-90"
                    >
                      <Youtube size={11} />
                      🎬 영상으로 복습 ({approvedVideos.length})
                    </button>
                  ) : recruitingVideos.length > 0 ? (
                    <button
                      onClick={() => setVideoModal(true)}
                      className="ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-bold transition-all shadow-sm bg-violet-100 text-violet-700 border border-violet-300 hover:bg-violet-200"
                    >
                      <Youtube size={11} />
                      🎬 영상 모집 중
                    </button>
                  ) : (
                    <button
                      onClick={() => setVideoModal(true)}
                      className="ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-bold transition-all shadow-sm bg-slate-100 text-slate-400 hover:bg-slate-200"
                    >
                      <Youtube size={11} />
                      영상 복습
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* 파편 학습 영상 모달 */}
      {videoModal && (
        <ConceptVideoModal
          conceptTag={prob.concept}
          conceptLabel={prob.concept}
          onClose={() => setVideoModal(false)}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// 레벨 섹션 컴포넌트
// ────────────────────────────────────────────────────────────────────────────
function LevelSection({ level, problems, openIds, onToggle, onSolved }: {
  level: keyof typeof LEVEL_CONFIG
  problems: Problem[]
  openIds: Set<string>
  onToggle: (id: string) => void
  onSolved: (problemId: string, opts: { hintOpened: boolean; solutionOpened: boolean }) => void
}) {
  const lv = LEVEL_CONFIG[level]
  return (
    <div className="mb-8">
      <div className={`flex items-center gap-3 mb-4 p-4 rounded-2xl border-2 ${lv.light}`}>
        <div className={`w-10 h-10 rounded-xl ${lv.badge} flex items-center justify-center text-lg`}>
          {lv.icon}
        </div>
        <div>
          <h2 className="text-base font-black text-slate-800">{lv.label} 문제</h2>
          <p className="text-xs text-slate-500">{lv.desc} · {problems.length}문항</p>
        </div>
        <div className="ml-auto">
          <span className={`text-xs px-3 py-1 rounded-full font-semibold border ${lv.color}`}>
            {problems.length}문제
          </span>
        </div>
      </div>
      <div className="space-y-3">
        {problems.map(prob => (
          <ProblemCard
            key={prob.id}
            prob={prob}
            isOpen={openIds.has(prob.id)}
            onToggle={() => onToggle(prob.id)}
            onSolved={onSolved}
          />
        ))}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// 메인 페이지
// ────────────────────────────────────────────────────────────────────────────
const UNIT_ID = 'F2-1'

export default function ProblemPage() {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set(['P01']))
  const [filterLevel, setFilterLevel] = useState<string>('all')

  // progressStore 연동
  const { init, markProblemSolved, getUnit } = useProgressStore(s => ({
    init: s.init, markProblemSolved: s.markProblemSolved, getUnit: s.getUnit,
  }))
  useEffect(() => { init('student_001') }, [])
  const unitData = getUnit(UNIT_ID)
  const solvedCount = Object.values(unitData.problems).filter(p => p.solved).length

  const toggleId = (id: string) => {
    setOpenIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setOpenIds(new Set(PROBLEMS.map(p => p.id)))
  const collapseAll = () => setOpenIds(new Set())

  const levels: Array<keyof typeof LEVEL_CONFIG> = ['basic', 'standard', 'advanced', 'final']

  const filtered = filterLevel === 'all'
    ? PROBLEMS
    : PROBLEMS.filter(p => p.level === filterLevel)

  const groupedByLevel = levels.reduce((acc, lv) => {
    acc[lv] = filtered.filter(p => p.level === lv)
    return acc
  }, {} as Record<string, Problem[]>)

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* 헤더 */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
            <PenTool size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800">문제풀이 연습</h1>
            <p className="text-sm text-slate-500">일차함수 · 기본 ~ 서술형 15문제</p>
          </div>
        </div>

        {/* EBS 스타일 안내 배너 */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-4 mt-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">📺</div>
            <div>
              <p className="text-sm font-bold text-blue-800 mb-1">EBS 문제풀이 방식으로 학습하세요!</p>
              <p className="text-xs text-blue-600 leading-relaxed">
                문제를 클릭하여 펼치고 → 직접 풀어본 뒤 → 힌트로 방향을 잡고 → 단계별 풀이로 개념을 완성하세요.
                기본 → 실력 → 심화 → 종합 순서로 난이도가 올라갑니다.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 필터 + 전체 열기/닫기 */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {[{id:'all', label:'전체', icon:'📋'}, ...levels.map(lv => ({id:lv, label:LEVEL_CONFIG[lv].label, icon:LEVEL_CONFIG[lv].icon}))].map(f => (
            <button
              key={f.id}
              onClick={() => setFilterLevel(f.id)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition-all ${
                filterLevel === f.id
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {f.icon} {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={expandAll} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors flex items-center gap-1">
            <ChevronDown size={11} /> 전체 펼치기
          </button>
          <button onClick={collapseAll} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors flex items-center gap-1">
            <ChevronUp size={11} /> 전체 접기
          </button>
        </div>
      </div>

      {/* 진도 현황 배지 */}
      {solvedCount > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <CheckCircle size={14} className="text-emerald-500" />
          <span className="text-sm font-semibold text-emerald-700">{solvedCount} / 15 문제 풀이 완료</span>
          <div className="flex-1 bg-slate-100 rounded-full h-2 max-w-xs">
            <div className="h-2 rounded-full bg-emerald-400 transition-all" style={{ width: `${Math.round(solvedCount / 15 * 100)}%` }} />
          </div>
        </div>
      )}

      {/* 문제 섹션들 */}
      {levels.map(lv => (
        groupedByLevel[lv]?.length > 0 && (
          <LevelSection
            key={lv}
            level={lv}
            problems={groupedByLevel[lv]}
            openIds={openIds}
            onToggle={toggleId}
            onSolved={(problemId, opts) => markProblemSolved(UNIT_ID, problemId, opts)}
          />
        )
      ))}

      {/* 하단 안내 */}
      <div className="mt-8 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5 text-center">
        <div className="text-3xl mb-2">🎓</div>
        <p className="text-base font-bold text-emerald-800 mb-1">모든 문제를 풀었나요?</p>
        <p className="text-sm text-emerald-600 mb-3">
          문제풀이를 마쳤다면 실전 TEST 탭에서 실력을 확인해보세요!<br />
          점수 분석 + 틀린 문제 풀이까지 제공됩니다.
        </p>
        <div className="flex justify-center items-center gap-2 text-xs text-emerald-500">
          <Award size={14} />
          <span>실전 TEST는 오른쪽 탭에서 진행할 수 있습니다</span>
          <ArrowRight size={14} />
        </div>
      </div>
    </div>
  )
}
